import { useRecipeService } from "../../Hooks/useRecipeService";
import { LoadingWrapper } from "../../Shared/LoadingWrapper";
import { RecipeSavedSnackbar } from "./Components/RecipeSavedSnackbar";
import { RecipeForm } from "./Components/RecipeForm";
import React, { useState, useEffect } from "react";
import queryString from "query-string";
import UrlEntryModal from "./Components/UrlEntryModal";

// Error message constants
const IMPORT_ERROR_NO_RECIPE = "Import Error!\n\nThe recipe scraper failed to find a recipe at the URL provided.";

export function CreateRecipe(props) {
	const recipeService = useRecipeService(props.config);
	const queryStringValues = queryString.parse(props.location.search);
	const [recipe, setRecipe] = useState(setInitialRecipe());
	const [toastOpen, setToastOpen] = useState(false);
	const [resetSignal, setResetSignal] = useState(0);
	const [importUrl, setImportUrl] = useState("");
	const [isExecuting, setIsExecuting] = useState(false);
	const [isImporting, setIsImporting] = useState(false);
	const [newRecipeId, setNewRecipeId] = useState("");
	const [isLoading, setIsLoading] = useState(true);
	const [tags, setTags] = useState([]);
	const [isImport, setIsImport] = useState(queryStringValues.import);

	useEffect(() => {
		document.title = `New Recipe - ${props.config.appName}`;
		setIsLoading(true);
		recipeService.getTags((response) => {
			setTags((response.data || []).map(t => t.tagName || t));
			setIsLoading(false);
		}, (error) => {
			console.error(error);
			alert("Error getting list of tags.");
		});
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	function onToastClose() {
		setToastOpen(false);
		setNewRecipeId("");
	}

	function createRecipe(newRecipe) {
		setIsExecuting(true);
		return new Promise((resolve, reject) => {
			recipeService.createRecipe(newRecipe, (response) => {
				if (response && response.status === 200 && response.data) {
					resolve(response.data);
				} else {
					console.log(response);
					reject(new Error("Create failed"));
				}

				setIsExecuting(false);
			}, (error) => {
				console.log(error);
				if (error.response) {
					console.log(error.response);
				}

				setIsExecuting(false);
				reject(error);
			});
		});
	}

	function onAfterSave(savedRecipeId) {
		setNewRecipeId(savedRecipeId);
		setToastOpen(true);
		setRecipe(setInitialRecipe());
		setResetSignal((s) => s + 1);
	}

	function cancelCreateRecipe() {
		props.history.replace("/recipes");
	}

	function cancelImportRecipe() {
		setIsImport(false);
	}

	function onImportUrlChange(value) {
		setImportUrl(value);
		return value;
	}

	function onImportUrlEntryModalSubmit() {
		setIsImport(false);
		setIsImporting(true);
		recipeService.importRecipe(importUrl, (response) => {
			if (response && response.status === 200 && response.data) {
				// Check if the imported recipe has meaningful content
				const importedRecipe = response.data;
				const hasContent = importedRecipe.name?.trim() ||
					importedRecipe.ingredients?.trim() ||
					importedRecipe.instructions?.trim() ||
					(importedRecipe.tags && importedRecipe.tags.length > 0);

				if (hasContent) {
					setRecipe(importedRecipe);
				}
				else {
					// Treat empty recipe as an error
					alert(IMPORT_ERROR_NO_RECIPE);
				}
			}
			else {
				console.log(response);
				alert(IMPORT_ERROR_NO_RECIPE);
			}

			setIsImporting(false);
		}, (error) => {
			console.log(error);
			if (error.response) {
				console.log(error.response);
				// Show user-friendly error message
				const errorMessage = error.response.data ?? IMPORT_ERROR_NO_RECIPE;
				alert(`Import Error: ${errorMessage}`);
			}
			else {
				alert(IMPORT_ERROR_NO_RECIPE);
			}

			setIsImporting(false);
		});
	}

	function onImportClick() {
		setImportUrl("");
		setIsImport(true);
	}

	return (
		<React.Fragment>
			<LoadingWrapper isLoading={isLoading || isImporting}>
				<RecipeForm
					config={props.config}
					pageTitle="Create a new Recipe"
					recipe={recipe}
					tagOptions={tags}
					onSaveClick={createRecipe}
					onAfterSave={onAfterSave}
					onCancel={cancelCreateRecipe}
					onImportClick={onImportClick}
					resetSignal={resetSignal}
					isSaveExecuting={isExecuting || isImporting} />
				<RecipeSavedSnackbar
					toastOpen={toastOpen}
					onToastClose={onToastClose}
					recipeId={newRecipeId} />
			</LoadingWrapper>
			<UrlEntryModal
				isOpen={isImport}
				url={importUrl}
				onSubmit={onImportUrlEntryModalSubmit}
				onCancel={cancelImportRecipe}
				onUrlChange={onImportUrlChange} />
		</React.Fragment>
	);
}

function setInitialRecipe() {
	return {
		recipeId: "",
		name: "",
		tags: [],
		ingredients: "",
		instructions: "",
		notes: ""
	};
}
